import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { IconButton, Dropdown, tokens } from '@hotjar/hotsauce';
import { event } from '@hotjar/webapp/tracking/tools/hotjar';
import type { UIPosition } from '@hotjar/webapp/domains/highlights/api';
import { StandaloneShortLinkDropdown } from './StandaloneShortLinkDropdown';
import { ShortLinkDropdownSkeletonLoader } from './ShortLinkDropdownSkeletonLoader';

const RECORDING_HIGHLIGHT_SHARED = 'recording_highlight_shared';
const HEATMAP_HIGHLIGHT_SHARED = 'heatmap_highlight_shared';

export const ShortLinkWrapper = styled(Dropdown)`
  border: 1px solid ${tokens.color.ui_border};
  border-radius: ${tokens.radius.l};

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: ${tokens.spacer.x2} ${tokens.spacer.x3};

  ${tokens.elevation(4)};

  .tippy-content {
    padding: 0px;
  }
`;

interface ShortLinkProps {
  className?: string;
  highlightType: string;
  title?: string;
  uiPosition?: UIPosition;
  highlightId?: string | null;
}

export function ShortLink({
  className,
  highlightType,
  title,
  uiPosition,
  highlightId,
}: ShortLinkProps): JSX.Element | null {
  const { t } = useTranslation();

  const [isShown, setIsShown] = useState(false);

  const handleOnShown = (): void => {
    setIsShown(true);
    if (highlightType === 'heatmap') {
      event(HEATMAP_HIGHLIGHT_SHARED);
    }

    if (highlightType === 'recording') {
      event(RECORDING_HIGHLIGHT_SHARED);
    }
  };

  const onDropdownClosed = () => {
    setIsShown(false);
  };

  return (
    <ShortLinkWrapper
      onShown={handleOnShown}
      onHidden={onDropdownClosed}
      appendTo="parent"
      trigger={
        <IconButton
          className={className}
          icon="share"
          kind="ghost"
          size="s"
          tooltipText={t('common:actions.share')}
          description={t('common:actions.share')}
        />
      }
    >
      {isShown ? (
        <StandaloneShortLinkDropdown
          title={title}
          highlightId={highlightId}
          uiPosition={uiPosition}
        />
      ) : (
        <ShortLinkDropdownSkeletonLoader />
      )}
    </ShortLinkWrapper>
  );
}
